/**
 * 任务（航线 / 围栏 / 备降点）的本地模型，以及它与线上表示之间的转换。
 *
 * 三条不变量，后面所有代码都靠它们成立：
 *
 * 1. **本地一律是 WGS84 十进制度（double）与米。** 线上 `MISSION_ITEM_INT` 的 x/y 是
 *    int32 的 degE7，换算只走 `@hgc/ardupilot` 的 units —— 不在这里自己写一份。
 *    那一份里已经付过两个坑的代价（`Math.trunc` 会让赤道附近出现一条"吸附带"、
 *    `-0` 会在快照比对里制造无意义差异），重写一遍等于把坑重新挖开。
 *
 * 2. **单位后缀写进字段名**（latDeg / lonDeg / altM）。任务数据是整个地面站里最容易
 *    "差 1e7 倍"的地方，而这类错误不会抛异常、不会有报错，只会有一架飞错地方的飞机。
 *
 * 3. **`seq` 就是下标。** MAVLink 里任务项的顺序由 `seq` 唯一决定，机体也只认
 *    "第 seq 条"，所以本地允许先构造再规范化（{@link normalizePlan}），
 *    但发到链路上之前一定是 0..n-1。
 */
import { degToLatLonE7 } from '@hgc/ardupilot';
import { enumByName, enumValue, type MessageValues } from '@hgc/mavlink';

import type { MissionItemEvent } from '../vehicle';

// ---------------------------------------------------------------------------
// MAV_MISSION_TYPE
// ---------------------------------------------------------------------------

/** `MAV_MISSION_TYPE` 里本包支持的三个值（255 = 全部，只用于清空，不作为任务类型） */
export type MissionTypeId = 0 | 1 | 2;

export const MISSION_TYPE_MISSION: MissionTypeId = 0;
export const MISSION_TYPE_FENCE: MissionTypeId = 1;
export const MISSION_TYPE_RALLY: MissionTypeId = 2;

export const MISSION_TYPES: readonly MissionTypeId[] = [
  MISSION_TYPE_MISSION,
  MISSION_TYPE_FENCE,
  MISSION_TYPE_RALLY,
];

export const MISSION_TYPE_LABELS: Readonly<Record<MissionTypeId, string>> = Object.freeze({
  [MISSION_TYPE_MISSION]: '航线',
  [MISSION_TYPE_FENCE]: '围栏',
  [MISSION_TYPE_RALLY]: '备降点',
});

/**
 * 一条任务项在界面上的中文量词。
 *
 * 存在的理由只有一条：报错信息要能落到具体某一行的中文说法上 ——
 * "第 3 个围栏点的高度超出范围"比"第 3 条任务项有问题"更容易让人找到该改哪一行。
 */
export const MISSION_TYPE_ITEM_NOUNS: Readonly<Record<MissionTypeId, string>> = Object.freeze({
  [MISSION_TYPE_MISSION]: '航点',
  [MISSION_TYPE_FENCE]: '围栏点',
  [MISSION_TYPE_RALLY]: '备降点',
});

export function isMissionTypeId(value: number): value is MissionTypeId {
  return value === 0 || value === 1 || value === 2;
}

export function missionTypeLabel(missionType: number): string {
  return isMissionTypeId(missionType) ? MISSION_TYPE_LABELS[missionType] : `未知任务类型 ${missionType}`;
}

export function missionItemNoun(missionType: number): string {
  return isMissionTypeId(missionType) ? MISSION_TYPE_ITEM_NOUNS[missionType] : '任务项';
}

// ---------------------------------------------------------------------------
// MAV_FRAME
// ---------------------------------------------------------------------------

/**
 * 默认任务框架：`MAV_FRAME_GLOBAL_RELATIVE_ALT`（相对起飞点的高度）。
 *
 * 从生成的枚举表取，取不到才退回标准值 3：枚举表损坏时"写出一个无效框架"
 * 比"写出一个正确但写死的框架"糟得多。多旋翼的日常航线就是相对高度，
 * 拿它当默认值是刻意的 —— 绝对高度的默认值会让飞机按海拔爬升到山上去。
 */
export const MAV_FRAME_GLOBAL_RELATIVE_ALT =
  enumValue('MAV_FRAME', 'MAV_FRAME_GLOBAL_RELATIVE_ALT') ?? 3;

let frameNames: Map<number, string> | null = null;

/** `MAV_FRAME` 数值 → 枚举名；查不到返回 null */
export function missionFrameName(frame: number): string | null {
  if (frameNames === null) {
    frameNames = new Map();
    for (const entry of enumByName('MAV_FRAME')?.entries ?? []) frameNames.set(entry.value, entry.name);
  }
  return frameNames.get(frame) ?? null;
}

/**
 * 是不是"带全局坐标"的框架（`MAV_FRAME_GLOBAL*`）。
 *
 * 判断依据是枚举名的前缀而不是数字：`MAV_FRAME_GLOBAL_INT` 这类 _INT 变体
 * 与 `MAV_FRAME_GLOBAL` 数值相邻，手写数字表迟早会错位
 * （而错位的表现是"所有航线项跑到几内亚湾"）。
 */
export function isGlobalMissionFrame(frame: number): boolean {
  const name = missionFrameName(frame);
  return name !== null && name.startsWith('MAV_FRAME_GLOBAL');
}

// ---------------------------------------------------------------------------
// 本地模型
// ---------------------------------------------------------------------------

/** 一条任务项。字段与 `MISSION_ITEM_INT` 基本一一对应，只有坐标换了表示。 */
export interface MissionItem {
  /** 序号，就是它在列表里的下标；见文件头的第 3 条 */
  readonly seq: number;
  /** `MAV_CMD` */
  readonly command: number;
  /** `MAV_FRAME` */
  readonly frame: number;
  /** 是不是"从这一条开始执行"。飞控把它当成当前执行位置，不是无意义的标志位 */
  readonly current: boolean;
  /** 到达这一条之后是否自动继续下一条；关掉它等于让飞机在这里等下一个指令 */
  readonly autocontinue: boolean;
  readonly param1: number;
  readonly param2: number;
  readonly param3: number;
  readonly param4: number;
  /**
   * 纬度（十进制度，double）。只对全局框架有意义 ——
   * 本地框架（NED）下飞控把 x/y 当相对原点的米用，本包把这类条目在校验里报成错误，
   * 所以"把米当度"这件事不会静默地流过上传流程。
   */
  readonly latDeg: number;
  /** 经度（十进制度，double） */
  readonly lonDeg: number;
  /** 高度（米）；是绝对高度还是相对起飞点由 `frame` 决定 */
  readonly altM: number;
}

/** 一份任务：类型 + 有序条目 */
export interface MissionPlan {
  readonly missionType: MissionTypeId;
  readonly items: readonly MissionItem[];
}

/** 返航点（`HOME_POSITION` / `MAV_CMD_DO_SET_HOME`） */
export interface MissionHomePosition {
  readonly latDeg: number;
  readonly lonDeg: number;
  /** 海拔（MSL，米）—— `HOME_POSITION.altitude` 的定义就是 MSL */
  readonly altM: number;
}

/** {@link createMissionItem} 的入参：坐标与高度之外全部可选，未给的按 MAVLink 的约定取零 */
export interface MissionItemInit {
  readonly command: number;
  readonly latDeg?: number;
  readonly lonDeg?: number;
  readonly altM?: number;
  readonly frame?: number;
  readonly current?: boolean;
  readonly autocontinue?: boolean;
  readonly param1?: number;
  readonly param2?: number;
  readonly param3?: number;
  readonly param4?: number;
}

/** 造一条任务项。`seq` 先给 0，由 {@link normalizePlan} 按下标重排。 */
export function createMissionItem(init: MissionItemInit): MissionItem {
  return {
    seq: 0,
    command: init.command,
    frame: init.frame ?? MAV_FRAME_GLOBAL_RELATIVE_ALT,
    current: init.current ?? false,
    // 默认自动继续：把默认值设成"停在原地"会让"上传完就悬停在第一个航点"成为默认行为
    autocontinue: init.autocontinue ?? true,
    param1: init.param1 ?? 0,
    param2: init.param2 ?? 0,
    param3: init.param3 ?? 0,
    param4: init.param4 ?? 0,
    latDeg: init.latDeg ?? 0,
    lonDeg: init.lonDeg ?? 0,
    altM: init.altM ?? 0,
  };
}

/** 用一串入参造一份任务，序号按数组下标自动排好 */
export function createPlan(missionType: MissionTypeId, items: readonly MissionItemInit[]): MissionPlan {
  return normalizePlan({ missionType, items: items.map(createMissionItem) });
}

/** 按下标重排 `seq`（其余字段原样保留），返回新对象 */
export function normalizePlan(plan: MissionPlan): MissionPlan {
  const items = plan.items.map((item, index) => (item.seq === index ? item : { ...item, seq: index }));
  return { missionType: plan.missionType, items };
}

export function emptyPlan(missionType: MissionTypeId): MissionPlan {
  return { missionType, items: [] };
}

/** 一条任务项的经纬度对应的 degE7 整数（比对与出帧都用它，避免各处手写换算） */
export function itemLatLonE7(item: MissionItem): { readonly latE7: number; readonly lonE7: number } {
  return { latE7: degToLatLonE7(item.latDeg), lonE7: degToLatLonE7(item.lonDeg) };
}

/**
 * 从车辆归集出来的 `missionItem` 事件造一条本地任务项。
 *
 * `event.latDeg` 已经由 `Vehicle` 按消息版本解释过了：float 版 `MISSION_ITEM` 的 x/y 是
 * **十进制度**，INT 版是 degE7。这里必须直接用它的解释结果 ——
 * 自己再按"整数"解释一次 float 版，就是模拟机测试里钉住的那类 bug（经度会错到外海）。
 *
 * 非全局框架下事件里没有经纬度（只有相对原点的米），只能填 0；校验会把这类条目报成错误，
 * 所以这个 0 不会被当成有效坐标发回链路上。
 */
export function itemFromMissionItemEvent(event: MissionItemEvent): MissionItem {
  return {
    seq: event.seq,
    command: event.command,
    frame: event.frameRaw,
    current: event.current,
    autocontinue: event.autocontinue,
    param1: event.param1,
    param2: event.param2,
    param3: event.param3,
    param4: event.param4,
    latDeg: event.latDeg ?? 0,
    lonDeg: event.lonDeg ?? 0,
    altM: event.altM ?? 0,
  };
}

export interface WireItemOptions {
  /** 发 `MISSION_ITEM_INT`（true）还是 float 版 `MISSION_ITEM`（false） */
  readonly int: boolean;
  readonly missionType: MissionTypeId;
  readonly targetSystem: number;
  readonly targetComponent: number;
}

/**
 * 一条任务项 → 线上字段值。
 *
 * INT 版的 x/y 是 degE7 整数，float 版的 x/y 是十进制度 —— 两者绝不能混：
 * 把度当整数发出去，机体收到的纬度会小 7 个数量级。
 */
export function wireMissionItemValues(item: MissionItem, options: WireItemOptions): MessageValues {
  const e7 = itemLatLonE7(item);
  return {
    param1: item.param1,
    param2: item.param2,
    param3: item.param3,
    param4: item.param4,
    x: options.int ? e7.latE7 : item.latDeg,
    y: options.int ? e7.lonE7 : item.lonDeg,
    // z 在两个版本里都是 float 米，不参与 ×1e7
    z: item.altM,
    seq: item.seq,
    command: item.command,
    target_system: options.targetSystem,
    target_component: options.targetComponent,
    frame: item.frame,
    current: item.current ? 1 : 0,
    autocontinue: item.autocontinue ? 1 : 0,
    mission_type: options.missionType,
  };
}
